import { useCallback, useEffect, useMemo } from 'react'
import { ReadSyncValidationsDocument } from 'generated/resources/graphql'
import { print } from 'graphql'

import { useLazyQuery } from 'modules/core/hooks/useQuery'

import { usePrivateIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'
import {
  SyncIssue,
  useCompensationsSyncIssues,
} from 'modules/integrations/hooks/SyncContext/issues'

interface FetchIssuesResult {
  hasIssues: boolean
}

interface UseSyncIssuesLazyQueryResult {
  fetchIssues: () => Promise<FetchIssuesResult | undefined>
  issues: SyncIssue[]
  issuesLoading: boolean
  anyValidSyncable: boolean
  totalSyncables: number
}

export const useSyncIssuesLazyQuery = (): UseSyncIssuesLazyQueryResult => {
  const {
    syncEnabled,
    marketplaceIntegration,
    syncParameters,
    getDataCollectionSource,
    configuration,
    isBookkeeperView,
    companyId,
    setHasSyncables,
  } = usePrivateIntegrationsSyncContext()
  const [
    fetchSyncValidationsQuery,
    { data: syncValidationsData, loading: issuesLoading, networkActivity },
  ] = useLazyQuery(ReadSyncValidationsDocument)

  const validationNode = syncValidationsData?.integrations?.syncValidationsConnection.nodes[0]
  const rawIssues = useMemo(() => validationNode?.issues ?? [], [validationNode])
  const totalSyncables = validationNode?.totalSyncables ?? 0
  const invalidSyncables = validationNode?.invalidSyncables ?? 0
  const anyValidSyncable = invalidSyncables < totalSyncables

  useEffect(() => {
    if (syncValidationsData !== undefined) {
      setHasSyncables(totalSyncables > 0)
    }
  }, [syncValidationsData, totalSyncables, setHasSyncables])

  const fetchIssues = useCallback(async (): Promise<FetchIssuesResult | undefined> => {
    if (!syncEnabled || !marketplaceIntegration || !companyId) return undefined

    const dataCollectionSource = getDataCollectionSource()
    const mappedVariables = dataCollectionSource?.mappedVariables
    const queryForSync = dataCollectionSource?.document
      ? print(dataCollectionSource.document)
      : null
    const graphqlVariablesJson =
      queryForSync && mappedVariables
        ? JSON.stringify({
            query: queryForSync,
            variables: { ...mappedVariables },
          })
        : undefined

    const result = await fetchSyncValidationsQuery({
      variables: {
        companyId,
        marketplaceIntegrationId: marketplaceIntegration.id,
        origin: configuration.origin,
        parameters: syncParameters(),
        graphqlVariablesJson,
      },
      context: {
        isBookkeeperView,
      },
    })

    const node = result.data?.integrations?.syncValidationsConnection.nodes[0]
    const fetchedIssues = node?.issues ?? []

    return { hasIssues: fetchedIssues.length > 0 }
  }, [
    syncEnabled,
    marketplaceIntegration,
    companyId,
    getDataCollectionSource,
    configuration.origin,
    syncParameters,
    isBookkeeperView,
    fetchSyncValidationsQuery,
  ])

  const applyMissingAmountFilter = useCallback(() => {
    const source = getDataCollectionSource()
    if (source) {
      const setFilters = source.setCurrentFilters
      if (typeof setFilters === 'function') {
        setFilters({ validationTypes: ['pending_to_define'] })
      }
    }
  }, [getDataCollectionSource])

  return {
    fetchIssues,
    issues: useCompensationsSyncIssues(
      rawIssues,
      marketplaceIntegration,
      true,
      applyMissingAmountFilter
    ),
    issuesLoading: networkActivity || issuesLoading,
    anyValidSyncable,
    totalSyncables,
  }
}
